/**
 * "Use this stamp": tapping a stamp in the stamp book readies its class for the
 * next letter to that person. The composer picks it up when it opens. Express
 * is still never spent on its own — the member seals the letter, and the server
 * takes the stamp then, as for any Express letter.
 *
 * Session-scoped and short-lived: an intent outlives a navigation, not a closed
 * tab, a sign-out (a shared device must not hand the next member someone
 * else's choice) or half an hour.
 */

import type { PostageClass } from '@toiyp/shared';

const PREFIX = 'toiyp:postage-intent:';
const TTL_MS = 30 * 60 * 1000;
const memory = new Map<string, { cls: PostageClass; at: number }>();

export function readyClass(relationshipId: string, cls: PostageClass): void {
  const entry = { cls, at: Date.now() };
  memory.set(relationshipId, entry);
  try {
    sessionStorage.setItem(PREFIX + relationshipId, JSON.stringify(entry));
  } catch {
    /* storage unavailable: the in-memory copy still serves this tab */
  }
}

/** The readied class for this pair, once — reading it clears it. */
export function takeReadiedClass(relationshipId: string): PostageClass | null {
  let entry = memory.get(relationshipId) ?? null;
  memory.delete(relationshipId);
  try {
    const raw = sessionStorage.getItem(PREFIX + relationshipId);
    sessionStorage.removeItem(PREFIX + relationshipId);
    if (!entry && raw) entry = JSON.parse(raw) as { cls: PostageClass; at: number };
  } catch {
    /* ignore */
  }
  return entry && Date.now() - entry.at <= TTL_MS ? entry.cls : null;
}

/** Forget every readied stamp — on sign-out. */
export function clearReadiedClasses(): void {
  memory.clear();
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(PREFIX)) sessionStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
}
